import type { CoprincipalAccountSource } from 'owa-account-source-list-store';
import {
    getArchiveMailboxInfo,
    getCoprincipalAccountByMailboxGuid,
    getCoprincipalAccountForAccountKey,
    getSharedMailboxInfo,
    getCoprincipalAccountsByOidAndTid,
} from 'owa-account-source-list-store';
import { getAccountKeyForSourceId, type MailboxInfo } from 'owa-client-types';
import { getQueryStringParameter } from 'owa-querystring';

/**
 * Parsed components of the `ai` (authenticating identity) query parameter.
 * Supported formats:
 * - `<ID>@tenantID`
 * - `OID:<ID>@tenantID`
 * - `PUID:<ID>@tenantID`
 *
 * For backward compatibility we also tolerate `<OID>/<legacy>@tenantID` and
 * use the first segment as the OID.
 */
interface AuthenticatingIdentity {
    oid: string;
    tenantId: string;
}

/**
 * Parses the `ai` query parameter value into its component parts.
 * Expected primary format: `<ID>@tenantID` (already URL-decoded by query string parser)
 * Prefixes `OID:` and `PUID:` are accepted and normalized into `oid`.
 * @param ai The raw `ai` query parameter value
 * @returns Parsed identity components, or undefined if the format is invalid
 */
function parseAuthenticatingIdentity(ai: string | undefined): AuthenticatingIdentity | undefined {
    if (!ai) {
        return undefined;
    }

    const atIndex = ai.lastIndexOf('@');
    if (atIndex === -1) {
        return undefined;
    }

    const tenantId = ai.substring(atIndex + 1);
    const rawIdentity = ai.substring(0, atIndex);
    if (!tenantId || !rawIdentity) {
        return undefined;
    }

    // Back-compat: old values could be OID/legacyIdentifier@tenant.
    const firstIdentitySegment = rawIdentity.split('/')[0];
    if (!firstIdentitySegment) {
        return undefined;
    }

    // If the segment contains a colon, it must be a recognized prefix (OID: or PUID:).
    // Unrecognized prefixed formats (e.g. SMTP:) are rejected to avoid misinterpreting
    // the value as a bare OID.
    const prefixMatch = firstIdentitySegment.match(/^(?:OID|PUID):(.*)$/i);
    if (!prefixMatch && firstIdentitySegment.includes(':')) {
        return undefined;
    }
    const oid = (prefixMatch ? prefixMatch[1] : firstIdentitySegment).trim();

    if (!oid) {
        return undefined;
    }

    return { oid, tenantId };
}

/**
 * Given an authenticating account's MailboxInfo, attempts to resolve a destination
 * mailbox that lives under that account (e.g., a shared mailbox or archive).
 * @param authenticatingMailboxInfo The MailboxInfo of the authenticating coprincipal account
 * @param destinationSmtpAddress The SMTP address of the destination mailbox
 * @returns The destination MailboxInfo if found under the account, or undefined
 */
function resolveMailboxFromAuthenticatingAccount(
    authenticatingMailboxInfo: MailboxInfo,
    destinationSmtpAddress: string
): MailboxInfo | undefined {
    const sharedMatch = getSharedMailboxInfo(authenticatingMailboxInfo, destinationSmtpAddress);
    if (sharedMatch) {
        return sharedMatch;
    }

    const archiveMatch = getArchiveMailboxInfo(authenticatingMailboxInfo, destinationSmtpAddress);
    if (archiveMatch) {
        return archiveMatch;
    }

    return undefined;
}

/**
 * Resolves a MailboxInfo from Universal Link URL components.
 *
 * URL format: `<domain>/<module>/<destinationMailboxGuid>/applink/<path>/?ds=smtp&ai=<ID>@tenantID`
 *
 * Resolution order:
 * 1. destinationMailboxGuid (from path) → getCoprincipalAccountByMailboxGuid
 * 2. ai query param (authenticating OID+tenantId) → getCoprincipalAccountsByOid
 * 3. ds query param (destination SMTP) under the authenticating account → shared/archive lookup
 * 4. Returns undefined if no match found (callers handle fallback actions)
 *
 * @param destinationMailboxGuid The `:routeKey` path parameter (destination mailbox GUID)
 * @returns The resolved MailboxInfo, or undefined if no matching account was found
 */
export function resolveMailboxFromRouteKey(
    destinationMailboxGuid: string | undefined
): MailboxInfo | undefined {
    // 1. Try GUID lookup from path parameter (destination mailbox GUID)
    if (destinationMailboxGuid) {
        const account = getCoprincipalAccountByMailboxGuid(destinationMailboxGuid);
        if (account) {
            return account.mailboxInfo;
        }
    }

    // 2. Try authenticating OID from query param
    const ai = getQueryStringParameter('ai');
    const identity = parseAuthenticatingIdentity(ai);
    if (identity) {
        const coprincipalAccounts = getCoprincipalAccountsByOidAndTid(
            identity.oid,
            identity.tenantId
        );
        const authMailboxInfo = getAuthenticatingAccountMailboxInfo(coprincipalAccounts);
        if (authMailboxInfo) {
            // 3. Check if the destination is a sub-resource (shared/archive) under the auth account
            const destinationSmtp = getQueryStringParameter('ds');
            if (destinationSmtp) {
                return resolveMailboxFromAuthenticatingAccount(authMailboxInfo, destinationSmtp);
            }

            // No destination SMTP provided — caller is responsible for fallback actions
            return undefined;
        }
    }

    // No matching account found — caller is responsible for fallback actions
    return undefined;
}

function getAuthenticatingAccountMailboxInfo(
    coprincipalAccounts: CoprincipalAccountSource[]
): MailboxInfo | undefined {
    if (coprincipalAccounts.length === 0) {
        return undefined;
    }

    if (coprincipalAccounts.length === 1) {
        return coprincipalAccounts[0].mailboxInfo;
    }

    // If multiple coprincipal accounts share the same OID+TID, we can operate under the
    // assumption that they share the same accountKey and we can return the MailboxInfo
    // belonging to the CoprincipalAccountSource of this accountKey
    const accountKey = getAccountKeyForSourceId(coprincipalAccounts[0].sourceId);

    return getCoprincipalAccountForAccountKey(accountKey)?.mailboxInfo;
}
